import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdLabel, CmdModal, CmdModalFooterButton } from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import ApiService from '../../../Services/ApiService';
import PermissionsService, {
  PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
  PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
} from '../../../Services/PermissionsService';
import { IGroupSelectOptions } from '../../Forms/CmdField';
import GroupSelectHookForm from '../../Forms/Fields/GroupSelectHookForm';
import { transformErrors, validateFields } from '../../../Services/HookFormService';

interface IFormProps {
  closeCallback: () => void;
  vehicleIds: number[];
  vehicleName?: string;
  default: boolean;
  updateTable?: (() => void) | undefined;
  customerIds?: number | null;
  availableTemplates: IGroupSelectOptions;
  templateId?: number | string;
}

interface ITemplateAssignInputForm {
  templateId: string;
}

export default function Form(props: IFormProps) {
  const { formatMessage: f } = useIntl();
  const [loader, setLoader] = React.useState<boolean>(false);
  const [options, setOptions] = React.useState<IGroupSelectOptions>({});

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ITemplateAssignInputForm>({
    mode: 'onChange',
    defaultValues: {
      templateId: props.templateId && props.templateId !== '-' ? String(props.templateId) : '',
    },
    resolver: async (data) => {
      const validateErros = validateFields(data, {
        templateId: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'waypoints.edit.title.required' }),
          },
        ],
      });

      const formErrors = transformErrors(validateErros);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  React.useEffect(() => {
    if (props.availableTemplates) {
      const filteredTemplates = { ...props.availableTemplates };

      const filteredSystemTemplates =
        filteredTemplates[f({ id: 'enum.onlineMapTemplate.type.system' })] &&
        PermissionsService.hasAccess(
          PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
          ['read'],
          props.customerIds
        )
          ? filteredTemplates[f({ id: 'enum.onlineMapTemplate.type.system' })]
          : [];

      filteredTemplates[f({ id: 'enum.onlineMapTemplate.type.system' })] = filteredSystemTemplates;

      const filteredCustomerTemplates =
        filteredTemplates[f({ id: 'enum.onlineMapTemplate.type.public' })] &&
        PermissionsService.hasAccess(
          PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
          ['read'],
          props.customerIds
        )
          ? filteredTemplates[f({ id: 'enum.onlineMapTemplate.type.public' })]
          : [];

      filteredTemplates[f({ id: 'enum.onlineMapTemplate.type.public' })] =
        filteredCustomerTemplates;

      setOptions(filteredTemplates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.availableTemplates]);

  const onSubmit = async (values) => {
    setLoader(true);
    values.vehicleId = props.vehicleIds;

    const response = props.default
      ? await ApiService.post(`/v1/online-map/assign/default`, values)
      : await ApiService.post(`/v1/online-map/assign`, values);
    setLoader(false);

    if (response && response.data && response.status === 200 && props.updateTable) {
      props.closeCallback();
      props.updateTable();
    }
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({
        id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.deleteCardConfirmDialog.cancel',
      }),
      tooltip: f({ id: 'alarms.modal.recipient.close.tooltip' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'onlinepanel-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'onlinepanel.modal.title.save' }),
      tooltip: props.default
        ? f({ id: 'onlinepanel.modal.title.default' })
        : f({ id: 'onlinepanel.modal.title' }),
      submitCallback: handleSubmit(onSubmit),
    },
  ];
  return (
    <div className="b-modal-overflow">
      <CmdModal
        id="template-assign-form-modal"
        title={
          props.vehicleIds.length > 1
            ? props.default
              ? f({ id: 'onlinepanel.multiple.modal.title.default' })
              : f({ id: 'onlinepanel.multiple.modal.title' })
            : props.default
              ? f({ id: 'onlinepanel.modal.title.default' })
              : f({ id: 'onlinepanel.modal.title' })
        }
        footerButtons={buttons}
        loader={loader}
      >
        {props.vehicleName && (
          <CmdLabel title={f({ id: 'tripsOverview.modal.vehicle' })} value={props.vehicleName} />
        )}
        <Controller
          name="templateId"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <GroupSelectHookForm
              {...field}
              id="templateId"
              label={f({ id: 'onlinepanel.templateId' })}
              placeholder={f({
                id: 'form.choose',
              })}
              options={options}
              newStyle={true}
              searchPlaceholder={f({ id: 'form.search' })}
              required={true}
              tooltip={f({ id: 'alarms.modal.rule.conditions.select.tooltip' })}
              // reload={isReset}
              control={control}
            />
          )}
        />
      </CmdModal>
    </div>
  );
}
